import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, ShieldCheck, Clock, Heart } from 'lucide-react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useCmsImages, useCmsContent } from '../context/CmsImagesContext';

export default function Home() {
  const { t } = useLanguage();
  const { getImage, getText, services, forsideServices, stats, testimonials, companyInfo } = useCmsContent();

  const activeServices = forsideServices && forsideServices.length > 0 ? forsideServices : services;

  const serviceImageKeyMap = {
    privatrengoring: 'service_private',
    erhvervsrengoring: 'service_business',
    flytterengoring: 'service_move'
  };

  const formatServiceDetails = (rawDetails) => {
    const localized = t(rawDetails);
    let items = [];

    if (Array.isArray(localized)) {
      items = localized;
    } else if (typeof localized === 'string' && localized.trim()) {
      const clean = localized
        .replace(/<\/?(li|p|br|div)[^>]*>/gi, '\n')
        .replace(/<[^>]+>/g, '');
      items = clean.split(/[\n\r•·]+/g);
    } else if (Array.isArray(rawDetails)) {
      items = rawDetails;
    }

    const cleanItems = items
      .map((it) => (typeof it === 'string' ? it : t(it)))
      .map((it) => {
        if (typeof it !== 'string') return '';
        return it
          .replace(/<[^>]+>/g, '')
          .replace(/^[0-9]+[\.\)]\s*/, '')
          .replace(/^[-*•·]\s*/, '')
          .trim();
      })
      .filter((it) => Boolean(it) && it.length > 1);

    return cleanItems;
  };

  useEffect(() => {
    document.title = "FTR Service ApS";
  }, []);

  return (
    <Layout className="bg-[#c1ebf9]">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-0 lg:pt-20 lg:pb-0 bg-[url('/images/ftr_background.png')] bg-cover bg-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide uppercase bg-white/60 text-brand-primary border border-white/40 mb-6 backdrop-blur-sm">
                  {getText('forside', 'hero_underoverskrift', getText('forside_hero', 'underoverskrift', t({ da: 'Billund - Varde - Vejle kommune', en: 'Billund - Varde - Vejle Municipality' })))}
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-tight mb-6">
                  {getText('forside', 'hero_overskrift', getText('forside_hero', 'overskrift', t({ da: 'Privat-, Erhverv- & Flytterengøring', en: 'Private, Business & Move-out Cleaning' })))}
                </h1>
                <p className="text-lg text-gray-600 mb-10 leading-relaxed max-w-2xl">
                  {getText('forside', 'hero_tekst', getText('forside_hero', 'tekst', t({ 
                    da: 'Professionel, grundig og fleksibel rengøring – når det passer dig. Hos FTR Service ApS tilbyder vi pålidelig rengøring til både private hjem og virksomheder.',
                    en: 'Professional, thorough and flexible cleaning – when it suits you. At FTR Service ApS, we offer reliable cleaning for both private homes and businesses.'
                  })))}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={getText('forside', 'hero_knap_link', getText('forside_hero', 'knap_link', '/kontakt'))}
                    className="flex items-center justify-center px-8 py-4 bg-[#85d7f9] text-white rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-lg shadow-[#85d7f9]/20"
                  >
                    {getText('forside', 'hero_knap_tekst', getText('forside_hero', 'knap_tekst', t({ da: 'Få et gratis tilbud', en: 'Get a free quote' })))}
                  </Link>
                  <Link
                    to={getText('forside', 'hero_knap_2_link', getText('forside_hero', 'knap_2_link', '/tjenester'))}
                    className="flex items-center justify-center px-8 py-4 bg-white text-gray-900 border border-gray-200 rounded-2xl font-bold text-lg hover:border-brand-primary hover:text-brand-primary transition-all"
                  >
                    {getText('forside', 'hero_knap_2_tekst', getText('forside_hero', 'knap_2_tekst', t({ da: 'Se vores tjenester', en: 'See our services' })))}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </motion.div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {(stats && stats.length > 0 ? stats : [
                  { value: '3', label: t({ da: 'Kommune dækket', en: 'Municipalities covered' }) },
                  { value: '50+', label: t({ da: 'Faste kunder', en: 'Regular customers' }) },
                  { value: '100%', label: t({ da: 'Kundetilfredshed', en: 'Customer satisfaction' }) },
                ]).map((stat, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                    <p className="text-3xl font-bold text-brand-primary mb-1">{stat.value}</p>
                    <p className="text-sm text-gray-500 font-medium">{typeof stat.label === 'object' ? t(stat.label) : stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6 mt-16 lg:mt-0 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#85d7f9]/10 rounded-full blur-3xl -z-0"></div>
                
                <div className="relative z-10">
                  <img
                    src={getImage('home_hero', '/images/cleaning_person.png', { width: 900 })}
                    alt="FTR Service Rengøring"
                    className="w-full h-auto block"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-[#b4e4f5] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#85d7f9]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase bg-[#85d7f9] text-white mb-6">
            {getText('forside', 'ydelser_underoverskrift', getText('forside_ydelser', 'underoverskrift', t({ da: 'Vores ydelser', en: 'Our Services' })))}
          </span>
          <p className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            {getText('forside', 'ydelser_overskrift', getText('forside_ydelser', 'overskrift', t({ da: 'Kvalitet, tryghed og god service', en: 'Quality, safety and good service' })))}
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {getText('forside', 'ydelser_tekst', getText('forside_ydelser', 'tekst', t({ 
              da: 'Vi lægger vægt på høj kvalitet, og vi er selvfølgelig forsikret, hvis noget mod forventning skulle gå galt.',
              en: 'We emphasize high quality, and we are of course insured if something against expectations should go wrong.'
            })))}
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {activeServices.map((service) => (
            <motion.div
              key={service.id}
              whileHover={{ y: -5 }}
              className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all flex flex-col"
            >
              <div className="h-48 overflow-hidden relative">
                <img
                  src={getImage(serviceImageKeyMap[service.id] || 'service_private', service.image, { width: 600 })}
                  alt={t(service.title)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="p-8 flex-grow flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t(service.title)}</h3>
                {service.description && (
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {typeof service.description === 'string' ? service.description : t(service.description)}
                  </p>
                )}
                
                {(() => {
                  const points = formatServiceDetails(service.details);
                  if (points.length === 0) return null;
                  return (
                    <ul className="space-y-2 mb-8 mt-auto">
                      {points.map((detail, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-brand-primary mr-2 mt-0.5 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  );
                })()}

                <Link to={service.link} className="text-[#85d7f9] font-bold text-sm flex items-center hover:translate-x-1 transition-transform mt-auto">
                  {t({ da: 'Læs mere', en: 'Read more' })} <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tax Service Info */}
        <div className="max-w-3xl mx-auto mt-16 px-4">
          <div className="bg-white/40 border border-white/60 rounded-2xl p-6 text-center shadow-sm backdrop-blur-sm">
            <p className="text-gray-700 italic">
              {getText('forside', 'fradrag_tekst', getText('forside_fradrag', 'tekst', t({ 
                da: 'Privatkunder kan benytte servicefradrag hos Skat – skatteværdien er ca. 26% af arbejdslønnen.',
                en: 'Private customers can use service deductions at Skat – the tax value is approx. 26% of the labor cost.'
              })))}
            </p>
          </div>
        </div>
      </section>

      {/* Values/Why Us */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 leading-tight">
                {getText('forside', 'vaerdier_overskrift', getText('forside_vaerdier', 'overskrift', getText('forside_hvorfor_os', 'overskrift', t({ da: 'Hvorfor vælge FTR Service ApS?', en: 'Why choose FTR Service ApS?' }))))}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {getText('forside', 'vaerdier_tekst', getText('forside_vaerdier', 'tekst', getText('forside_hvorfor_os', 'tekst', t({ 
                  da: 'FTR Service ApS er en lokal rengøringsvirksomhed, der servicerer Billund, Varde og Vejle kommune. Vi går op i kvalitet, tillid og tilfredse kunder – og vi møder altid op til tiden.',
                  en: 'FTR Service ApS is a local cleaning company serving Billund, Varde and Vejle municipality. We care about quality, trust and satisfied customers – and we always show up on time.'
                }))))}
              </p>
              <div className="space-y-8">
                {[
                  { 
                    title: getText('forside', 'vaerdier_punkt_1_titel', getText('forside_vaerdier', 'punkt_1_titel', t({ da: 'Lokal ekspertise', en: 'Local expertise' }))), 
                    text: getText('forside', 'vaerdier_punkt_1_tekst', getText('forside_vaerdier', 'punkt_1_tekst', t({ da: 'Vi kender lokalområdet og servicerer Billund, Varde og Vejle kommune med stolthed.', en: 'We know the local area and serve Billund, Varde and Vejle municipality with pride.' }))), 
                    icon: <CheckCircle2 className="w-6 h-6" /> 
                  },
                  { 
                    title: getText('forside', 'vaerdier_punkt_2_titel', getText('forside_vaerdier', 'punkt_2_titel', t({ da: 'Kvalitet & Tillid', en: 'Quality & Trust' }))), 
                    text: getText('forside', 'vaerdier_punkt_2_tekst', getText('forside_vaerdier', 'punkt_2_tekst', t({ da: 'Vi lægger vægt på kvalitet, tryghed og god service i alt hvad vi gør.', en: 'We emphasize quality, safety and good service in everything we do.' }))), 
                    icon: <Clock className="w-6 h-6" /> 
                  },
                  { 
                    title: getText('forside', 'vaerdier_punkt_3_titel', getText('forside_vaerdier', 'punkt_3_titel', t({ da: 'Forsikret Service', en: 'Insured Service' }))), 
                    text: getText('forside', 'vaerdier_punkt_3_tekst', getText('forside_vaerdier', 'punkt_3_tekst', t({ da: 'Vi er selvfølgelig forsikret, hvis noget mod forventning skulle gå galt.', en: 'We are of course insured if something against expectations should go wrong.' }))), 
                    icon: <ShieldCheck className="w-6 h-6" /> 
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="bg-white/80 text-brand-primary p-3 rounded-xl h-fit shadow-sm">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg mb-1">{item.title}</h4>
                      <p className="text-gray-600 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 relative">
              <div className="absolute -inset-4 bg-white/80 rounded-3xl -rotate-1"></div>
              <div className="space-y-4 pt-8">
                <img
                  src={getImage('home_focus_1', 'https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/private-cleaning.png', { width: 600 })}
                  alt="Privat rengøring"
                  className="relative rounded-2xl shadow-lg w-full object-cover aspect-[3/4]"
                />
                <img
                  src={getImage('home_focus_2', 'https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/window-cleaning.png', { width: 600 })}
                  alt="Vinduespolering"
                  className="relative rounded-2xl shadow-lg w-full object-cover aspect-square"
                />
              </div>
              <div className="space-y-4">
                <img
                  src={getImage('home_focus_3', 'https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/business-cleaning.png', { width: 600 })}
                  alt="Erhvervsrengøring"
                  className="relative rounded-2xl shadow-lg w-full object-cover aspect-square"
                />
                <img
                  src={getImage('home_focus_4', 'https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/kitchen-cleaning.png', { width: 600 })}
                  alt="Køkkenrengøring"
                  className="relative rounded-2xl shadow-lg w-full object-cover aspect-[3/4]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Testimonials */}
      <section className="py-24 bg-[#b4e4f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {getText('forside', 'anmeldelser_overskrift', getText('forside_anmeldelser', 'overskrift', t({ da: 'Det siger vores kunder', en: 'What our customers say' })))}
            </h2>
            <p className="text-lg text-gray-600">
              {getText('forside', 'anmeldelser_tekst', getText('forside_anmeldelser', 'tekst', t({ da: 'Vi lever af glade og tilfredse kunder i Billund, Varde og Vejle kommune.', en: 'We live on happy and satisfied customers in Billund, Varde and Vejle municipality.' })))}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm flex flex-col h-full">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 italic mb-6 flex-grow">"{t(testimonial.text)}"</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{t(testimonial.role)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -ml-48 -mb-48"></div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {getText('forside', 'cta_overskrift', getText('forside_cta', 'overskrift', t({ da: 'Klar til et renere indeklima?', en: 'Ready for a cleaner indoor climate?' })))}
          </h2>
          <p className="text-blue-50 text-lg mb-10 opacity-90">
            {getText('forside', 'cta_tekst', getText('forside_cta', 'tekst', t({ 
              da: 'Kontakt os i dag for en uforpligtende samtale om dine rengøringsbehov. Vi giver gerne et fast tilbud.',
              en: 'Contact us today for a non-binding conversation about your cleaning needs. We are happy to give a fixed quote.'
            })))}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to={getText('forside', 'cta_knap_link', getText('forside_cta', 'knap_link', '/kontakt'))}
              className="bg-[#85d7f9] text-white px-8 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-xl shadow-[#85d7f9]/20"
            >
              {getText('forside', 'cta_knap_tekst', getText('forside_cta', 'knap_tekst', t({ da: 'Få et uforpligtende tilbud', en: 'Get a non-binding quote' })))}
            </Link>
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
              className="bg-[#85d7f9] text-white border border-[#85d7f9]/20 px-8 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-xl shadow-[#85d7f9]/20"
            >
              {t({ da: 'Ring til os', en: 'Call us' })}: {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
